import { RegisterActions } from '../enums/RegisterSteps'
import { DEFAULT_STATE } from '../hooks/useAppState'

export const registerReducer = (prevState, action) => {
  switch (action.type) {
    case RegisterActions.RESET_REGISTRATION: {
      return {
        ...DEFAULT_STATE,
        // Preserve auth/session + language preferences
        user: prevState.user,
        firebaseUser: prevState.firebaseUser,
        selectedLanguage: prevState.selectedLanguage
      }
    }
    case RegisterActions.SET_ACTIVE_STEP:
      return { ...prevState, activeRegisterStep: action.payload?.activeRegisterStep }
    case RegisterActions.SAVE_REGISTRATION_DATA:
      return {
        ...prevState,
        name: action.payload?.name,
        phoneNumber: action.payload?.phoneNumber,
        email: action.payload?.email,
        // Roles fall back to prevState: unlike the step-1-owned fields above, they are also
        // written by step 2 and remove-partner, so a payload without a role must mean "keep",
        // never "wipe" — clearing goes through SET_ROLE / SET_PARTNER_ROLE instead.
        role: action.payload?.role ?? prevState.role,
        partnerName: action.payload?.partnerName,
        partnerPhoneNumber: action.payload?.partnerPhoneNumber,
        partnerEmail: action.payload?.partnerEmail,
        partnerRole: action.payload?.partnerRole ?? prevState.partnerRole,
        mainUserSsn: action.payload?.mainUserSsn,
        partnerUserSsn: action.payload?.partnerUserSsn,
        mainUserAddress: action.payload?.mainUserAddress,
        partnerUserAddress: action.payload?.partnerUserAddress
      }
    case RegisterActions.SET_IS_REGISTRATION_COMPLETED:
      return { ...prevState, isRegistrationCompleted: true }
    case RegisterActions.SET_FINAL_PRICE:
      return { ...prevState, finalPrice: action.payload?.finalPrice }
    case RegisterActions.SET_FORM_ERRORS:
      return { ...prevState, hasFormErrors: action.payload?.hasFormErrors }
    case RegisterActions.SET_SELECTED_ACTIVITIES:
      return { ...prevState, selectedActivities: action.payload?.selectedActivities }
    case RegisterActions.SET_IS_MAIN_USER_STUDENT:
      return { ...prevState, isMainUserStudent: action.payload?.isMainUserStudent }
    case RegisterActions.SET_IS_PARTNER_STUDENT:
      return { ...prevState, isPartnerStudent: action.payload?.isPartnerStudent }
    case RegisterActions.SET_IS_MAIN_USER_SIBLING:
      return { ...prevState, isMainUserSibling: action.payload?.isMainUserSibling }
    case RegisterActions.SET_IS_PARTNER_SIBLING:
      return { ...prevState, isPartnerSibling: action.payload?.isPartnerSibling }
    case RegisterActions.SET_IS_MAIN_USER_SENIOR:
      return { ...prevState, isMainUserSenior: action.payload?.isMainUserSenior }
    case RegisterActions.SET_IS_PARTNER_SENIOR:
      return { ...prevState, isPartnerSenior: action.payload?.isPartnerSenior }
    case RegisterActions.SET_ENROLLMENT_ID:
      return { ...prevState, enrollmentId: action.payload?.enrollmentId }
    case RegisterActions.SET_PAYMENT_METHOD:
      return { ...prevState, paymentMethod: action.payload?.paymentMethod }
    case RegisterActions.SET_IS_PAID:
      return { ...prevState, isPaid: action.payload?.isPaid }
    case RegisterActions.SET_PAYMENT_STATUS:
      return { ...prevState, paymentStatus: action.payload?.paymentStatus }
    case RegisterActions.SET_IS_REGISTRATION_SUBMITTED:
      return { ...prevState, isRegistrationSubmitted: true }
    case RegisterActions.SET_MAIN_USER_ACTIVE_PERIODS:
      return { ...prevState, mainUserActivePeriods: action.payload?.mainUserActivePeriods }
    case RegisterActions.SET_PARTNER_USER_ACTIVE_PERIODS:
      return { ...prevState, partnerUserActivePeriods: action.payload?.partnerUserActivePeriods }
    case RegisterActions.SET_MAIN_USER_PREVIOUS_DISCOUNTS:
      return {
        ...prevState,
        mainUserEnrolledActivityIds: action.payload?.userEnrolledActivityIds,
        mainUserEnrolledPeriodIds: action.payload?.userEnrolledPeriodIds,
        mainUserEnrolledActivityPeriods: action.payload?.userEnrolledActivityPeriods || [],
        mainUsedContinuationDiscountIds: action.payload?.usedContinuationDiscountIds,
        mainUsedContinuationDiscountActivities: action.payload?.usedContinuationDiscountActivities || []
      }
    case RegisterActions.SET_PARTNER_USER_PREVIOUS_DISCOUNTS:
      return {
        ...prevState,
        partnerUserEnrolledActivityIds: action.payload?.userEnrolledActivityIds,
        partnerUserEnrolledPeriodIds: action.payload?.userEnrolledPeriodIds,
        partnerUserEnrolledActivityPeriods: action.payload?.userEnrolledActivityPeriods || [],
        partnerUsedContinuationDiscountIds: action.payload?.usedContinuationDiscountIds,
        partnerUsedContinuationDiscountActivities: action.payload?.usedContinuationDiscountActivities || []
      }
    case RegisterActions.SET_OBFUSCATED_ENROLLMENT:
      return { ...prevState, obfuscatedEnrollment: action.payload?.obfuscatedEnrollment }
    case RegisterActions.SET_MAIN_USER_SSN:
      return { ...prevState, mainUserSsn: action.payload?.mainUserSsn }
    case RegisterActions.SET_PARTNER_USER_SSN:
      return { ...prevState, partnerUserSsn: action.payload?.partnerUserSsn }
    case RegisterActions.SET_ENROLLMENT_COMMENT:
      return { ...prevState, enrollmentComment: action.payload?.enrollmentComment }
    case RegisterActions.SET_ENROLLMENT_DISCOUNT_CODE:
      return { ...prevState, discountCode: action.payload?.code }
    case RegisterActions.SET_ENROLLMENT_REGISTERED_PARTNERS:
      return { ...prevState, registeredPartners: action.payload?.registeredPartners }
    case RegisterActions.SET_PAYFOR:
      return { ...prevState, payFor: action.payload?.payFor }
    case RegisterActions.SET_DISCOUNT_TYPE:
      return { ...prevState, discountType: action.payload?.discountType }
    case RegisterActions.SET_DISCOUNT_REGION:
      return { ...prevState, discountRegion: action.payload?.discountRegion }
    case RegisterActions.SET_INDIVIDUAL_FLOW:
      return { ...prevState, individualFlow: action.payload?.individualFlow }
    // Both role cases bail out when the value is unchanged. Roles are dispatched from effects
    // (the single-registration preselect in step 1, the role preselect in step 2) whose deps
    // include callback identities that change every render. Returning a fresh object for a
    // no-op write re-renders every mounted step, which re-fires the effect, which dispatches
    // again — an unbounded loop. useState used to absorb this via its Object.is bail-out;
    // useReducer only bails out when the reducer returns prevState itself.
    case RegisterActions.SET_ROLE:
      return prevState.role === action.payload?.role ? prevState : { ...prevState, role: action.payload?.role }
    case RegisterActions.SET_PARTNER_ROLE:
      return prevState.partnerRole === action.payload?.partnerRole
        ? prevState
        : { ...prevState, partnerRole: action.payload?.partnerRole }
    case RegisterActions.SET_EARLY_BIRD_BOOKING:
      return { ...prevState, earlyBirdBooking: action.payload?.earlyBirdBooking }
    case RegisterActions.SET_SINGLE_EVENT_QUANTITY:
      return { ...prevState, singleEventQuantity: action.payload?.singleEventQuantity }
    case RegisterActions.SET_CHILDREN_INPUT:
      return { ...prevState, childrenInput: action.payload?.childrenInput }
    default:
      return prevState
  }
}
